import {Component, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {EmployeeFormSections, FileWidget, Tabs} from '@core/components';
import {AttachmentService, EmployeeService, GroupService} from '@core/services';
import {Confirm} from '@core/modals/confirm/confirm';
import {ModalService} from '@core/services/modal.service';
import {CompanyStateService} from '@core/states';
import {AuthStateService} from '@auth/states';
import {Attachment, EmployeeRow, GroupItem} from '@core/types';
import {EMPLOYEE_ATTACHMENT_RELATED_TYPE, EMPLOYEE_ROLE_OPTIONS} from '@core/utils';

@Component({
  selector: 'core-modal-edit-employee',
  imports: [
    EmployeeFormSections,
    FileWidget,
    ReactiveFormsModule,
    Tabs
  ],
  templateUrl: './edit-employee.html',
  styleUrl: './edit-employee.scss',
  standalone: true,
  host: {
    style: 'width: 100%;display: flex;flex-direction: column;gap: 1.25rem;padding: 0 1.5rem 1.5rem'
  }
})
export class EditEmployee implements OnInit, OnDestroy {
  @Input() employee: EmployeeRow | null = null;

  form: FormGroup;
  roleOptions = EMPLOYEE_ROLE_OPTIONS;
  groupOptions: {label: string; value: string | number}[] = [];
  availableGroups: GroupItem[] = [];
  isLoadingOptions = false;
  isLoadingDetail = false;
  isSubmitting = false;
  errorMessage = '';
  avatarFile: File | null = null;
  avatarPreviewUrl = '';
  currentAvatarUrl = '';
  contractFile: File | null = null;
  pendingAttachments: File[] = [];
  existingAttachments: Attachment[] = [];
  currentGroupId: string | number | null = null;
  currentIsGroupManager = false;

  private readonly modal = inject(ModalService);
  private readonly employeeService = inject(EmployeeService);
  private readonly groupService = inject(GroupService);
  private readonly attachmentService = inject(AttachmentService);
  private readonly companyState = inject(CompanyStateService);
  private readonly authState = inject(AuthStateService);
  private readonly formBuilder = inject(FormBuilder);

  constructor() {
    this.form = this.formBuilder.group({
      fullName: ['', [Validators.required]],
      email: ['', [Validators.required, Validators.email]],
      position: [null],
      groupId: [null],
      isGroupManager: [false],
      hiredAt: [''],
      activityKey: ['no'],
      sensitiveData: ['no'],
      cashControl: ['no'],
    });
  }

  ngOnInit() {
    this.patchFormFromEmployee();
    this.loadOptions();
    this.loadDetail();
  }

  ngOnDestroy() {
    this.revokeAvatarPreview();
    this.revokeCurrentAvatar();
  }

  async submit() {
    if (this.isSubmitting) return;
    if (this.form.invalid || !this.employee?.id) {
      this.form.markAllAsTouched();
      return;
    }

    this.isSubmitting = true;
    this.errorMessage = '';
    this.form.disable({emitEvent: false});

    const value = this.form.getRawValue();
    const {firstName, lastName} = this.splitName(value.fullName || '');
    const metadata: Record<string, any> = {
      contractFileName: this.contractFile?.name || this.employee?.metadata?.contractFileName || undefined,
      activityKey: value.activityKey,
      sensitiveData: value.sensitiveData,
      cashControl: value.cashControl
    };

    if (!this.isOwnerEditingSelf) {
      metadata['position'] = value.position || undefined;
    }

    const response = await this.employeeService.updateEmployee(this.employee.id, {
      firstName,
      lastName,
      email: value.email,
      hiredAt: value.hiredAt || undefined,
      groupId: value.groupId || undefined,
      isGroupManager: !!value.isGroupManager,
      metadata
    }, {
      skipLoader: true
    });

    if (!response.success) {
      this.isSubmitting = false;
      this.form.enable({emitEvent: false});
      this.errorMessage = response.message || 'No se pudo actualizar el empleado.';
      return;
    }

    const syncManagerResult = await this.syncManagerAssignment(
      this.employee.id,
      value.groupId || null,
      !!value.isGroupManager
    );

    if (!syncManagerResult.success) {
      this.isSubmitting = false;
      this.form.enable({emitEvent: false});
      this.errorMessage = syncManagerResult.message || 'Se actualizaron los datos, pero falló la sincronización del responsable.';
      return;
    }

    const uploadResult = await this.uploadSelectedDocuments(this.employee.id);
    if (!uploadResult.success) {
      this.isSubmitting = false;
      this.form.enable({emitEvent: false});
      this.errorMessage = uploadResult.message || 'Se actualizaron los datos, pero no se pudieron subir los documentos.';
      return;
    }

    this.isSubmitting = false;
    this.modal.closeTop(true);
  }

  async confirmDeactivate() {
    if (!this.employee?.id || this.isSubmitting) return;

    const ref = this.modal.open<Confirm, boolean>(Confirm, {
      inputs: {
        title: 'Dar de baja empleado',
        description: 'Se registrara la baja del empleado. ¿Deseas continuar?',
        confirmText: 'Dar de baja',
        cancelText: 'Cancelar'
      },
      outputs: {
        confirm: async () => {
          const response = await this.employeeService.deactivateEmployee(this.employee!.id as string | number);
          if (!response.success) {
            this.errorMessage = response.message || 'No se pudo dar de baja al empleado.';
            return;
          }
          this.modal.closeTop(true);
        }
      },
      options: {size: 'sm', closeOnBackdrop: false}
    });

    ref.afterClosed$.subscribe();
  }

  close() {
    if (this.isSubmitting) return;
    this.modal.closeTop(false);
  }

  openAvatarPicker() {
    if (this.isSubmitting) return;
    const input = document.getElementById('edit-employee-avatar-input') as HTMLInputElement | null;
    input?.click();
  }

  handleAvatarSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] || null;
    this.avatarFile = file;
    this.setAvatarPreview(file);
    input.value = '';
  }

  get contractFileName() {
    return this.contractFile?.name || this.employee?.metadata?.contractFileName || '';
  }

  handleContractSelected(file: File | null) {
    this.contractFile = file;
  }

  handleDocumentsSelected(files: File[]) {
    this.pendingAttachments = [...this.pendingAttachments, ...files];
  }

  removePendingAttachment(index: number) {
    this.pendingAttachments = this.pendingAttachments.filter((_, currentIndex) => currentIndex !== index);
  }

  openDocumentsPicker() {
    if (this.isSubmitting) return;
    const input = document.getElementById('edit-employee-documents-input') as HTMLInputElement | null;
    input?.click();
  }

  handleDocumentsInput(event: Event) {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files || []);
    if (files.length) {
      this.handleDocumentsSelected(files);
    }
    input.value = '';
  }

  async downloadAttachment(attachment: Attachment) {
    if (!attachment?.id) return;

    const response = await this.attachmentService.downloadAttachment(attachment.id, undefined, {skipLoader: true});
    if (!response.success || !response.result) {
      this.errorMessage = response.message || 'No se pudo descargar el documento.';
      return;
    }

    this.errorMessage = '';
    const url = URL.createObjectURL(response.result);
    const link = document.createElement('a');
    link.href = url;
    link.download = attachment.fileName || 'documento';
    link.click();
    URL.revokeObjectURL(url);
  }

  async removeAttachment(attachment: Attachment) {
    if (!attachment?.id || this.isSubmitting) return;

    const response = await this.attachmentService.deleteAttachment(attachment.id, {skipLoader: true});
    if (!response.success) {
      this.errorMessage = response.message || 'No se pudo eliminar el documento.';
      return;
    }

    this.existingAttachments = this.existingAttachments.filter(item => String(item.id) !== String(attachment.id));
    this.errorMessage = '';
  }

  private patchFormFromEmployee() {
    if (!this.employee) return;

    this.currentGroupId = this.employee.groupId ?? null;
    this.form.patchValue({
      fullName: this.employee.name || '',
      email: this.employee.email || '',
      position: this.employee.metadata?.position || this.employee.userRole?.slug || null,
      groupId: this.employee.groupId ?? null,
      isGroupManager: false,
      hiredAt: this.normalizeDateInput(this.employee.hiredAtRaw || this.employee.since_at || ''),
      activityKey: this.employee.metadata?.activityKey || 'no',
      sensitiveData: this.employee.metadata?.sensitiveData || 'no',
      cashControl: this.employee.metadata?.cashControl || 'no',
    });
  }

  private async loadDetail() {
    if (!this.employee?.id) return;

    this.isLoadingDetail = true;
    const response = await this.employeeService.getEmployeeDetail(this.employee.id, {skipLoader: true});
    if (!response.success || !response.result) {
      this.isLoadingDetail = false;
      return;
    }

    this.employee = {
      ...this.employee,
      ...response.result,
      metadata: {
        ...(this.employee?.metadata || {}),
        ...(response.result.metadata || {})
      }
    };
    this.patchFormFromEmployee();
    this.existingAttachments = response.result.attachments || [];
    await this.resolveCurrentAvatar();
    this.isLoadingDetail = false;
  }

  private async loadOptions() {
    this.isLoadingOptions = true;
    this.errorMessage = '';

    const groupsResponse = await this.groupService.getGroups({skipLoader: true});

    if (groupsResponse.success && groupsResponse.result) {
      this.availableGroups = groupsResponse.result;
      this.groupOptions = groupsResponse.result.map(group => ({
        label: group.name,
        value: group.id
      }));

      const currentGroup = groupsResponse.result.find(group => String(group.id) === String(this.currentGroupId ?? ''));
      this.currentIsGroupManager = !!(currentGroup?.manager?.id && this.employee?.id && String(currentGroup.manager.id) === String(this.employee.id));
      this.form.patchValue({
        isGroupManager: this.currentIsGroupManager
      });
    }

    if (!groupsResponse.success) {
      this.errorMessage = 'No se pudieron cargar las opciones del formulario.';
    }

    this.isLoadingOptions = false;
  }

  private async syncManagerAssignment(
    employeeId: string | number,
    nextGroupId: string | number | null,
    shouldBeManager: boolean
  ): Promise<{success: boolean; message?: string}> {
    const previousGroupId = this.currentGroupId;

    if (this.currentIsGroupManager && previousGroupId && (!nextGroupId || String(previousGroupId) !== String(nextGroupId) || !shouldBeManager)) {
      const clearResponse = await this.groupService.updateManagerAssignment(previousGroupId, null, {skipLoader: true});
      if (!clearResponse.success) {
        return {success: false, message: clearResponse.message};
      }
    }

    if (shouldBeManager && nextGroupId && (!this.currentIsGroupManager || String(previousGroupId ?? '') !== String(nextGroupId))) {
      const setResponse = await this.groupService.setManager(nextGroupId, employeeId, {skipLoader: true});
      if (!setResponse.success) {
        return {success: false, message: setResponse.message};
      }
    }

    return {success: true};
  }

  private splitName(fullName: string) {
    const trimmed = fullName.trim();
    if (!trimmed) {
      return {firstName: '', lastName: ''};
    }

    const parts = trimmed.split(' ');
    const firstName = parts.shift() || '';
    const lastName = parts.join(' ');
    return {firstName, lastName};
  }

  private normalizeDateInput(value: string) {
    if (!value) return '';
    if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
      return value.slice(0, 10);
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return '';
    }

    return parsed.toISOString().slice(0, 10);
  }

  private async uploadSelectedDocuments(employeeId: string | number): Promise<{success: boolean; message?: string}> {
    const files = [this.avatarFile, this.contractFile, ...this.pendingAttachments].filter(Boolean) as File[];
    if (!files.length) {
      return {success: true};
    }

    const companyId = this.companyState.membership()?.company?.id;
    if (!companyId) {
      return {success: false, message: 'No se pudo identificar la empresa para subir los documentos.'};
    }

    const response = await this.attachmentService.uploadAttachments({
      companyId: String(companyId),
      files,
      relatedToType: EMPLOYEE_ATTACHMENT_RELATED_TYPE,
      relatedToId: employeeId,
      skipLoader: true
    });

    if (!response.success) {
      return {success: false, message: response.message};
    }

    this.avatarFile = null;
    this.revokeAvatarPreview();
    this.contractFile = null;
    this.pendingAttachments = [];
    await this.loadDetail();
    return {success: true};
  }

  get selectedGroups(): GroupItem[] {
    const groupId = this.form.getRawValue().groupId;
    return this.availableGroups.filter(group => String(group.id) === String(groupId ?? ''));
  }

  isSelectedGroupManager(groupId: string | number) {
    const selectedGroupId = this.form.getRawValue().groupId;
    return this.currentIsGroupManager || (this.form.getRawValue().isGroupManager && String(selectedGroupId ?? '') === String(groupId));
  }

  get influenceItems() {
    return [];
  }

  get isOwnerEditingSelf(): boolean {
    const currentUser = this.authState.user();
    const currentMembership = this.companyState.membership();
    if (!currentUser?.id || !currentMembership || !this.companyState.isOwnerMembership(currentMembership)) {
      return false;
    }

    return String(this.employee?.userId || '') === String(currentUser.id);
  }

  get contractAttachmentName() {
    return this.contractFile?.name || this.employee?.metadata?.contractFileName || '';
  }

  isContractAttachment(fileName?: string) {
    return !!fileName && !!this.contractAttachmentName && fileName === this.contractAttachmentName;
  }

  private setAvatarPreview(file: File | null) {
    this.revokeAvatarPreview();
    if (!file) {
      this.avatarPreviewUrl = '';
      return;
    }

    this.avatarPreviewUrl = URL.createObjectURL(file);
  }

  private revokeAvatarPreview() {
    if (!this.avatarPreviewUrl) return;
    URL.revokeObjectURL(this.avatarPreviewUrl);
    this.avatarPreviewUrl = '';
  }

  get displayedAvatarUrl() {
    return this.avatarPreviewUrl || this.currentAvatarUrl || '';
  }

  private async resolveCurrentAvatar() {
    this.revokeCurrentAvatar();
    const avatarAttachment = this.existingAttachments.find(attachment => (attachment.mimeType || '').startsWith('image/'));
    if (!avatarAttachment?.id) {
      return;
    }

    const response = await this.attachmentService.downloadAttachment(avatarAttachment.id, undefined, {skipLoader: true});
    if (!response.success || !response.result) {
      return;
    }

    this.currentAvatarUrl = URL.createObjectURL(response.result);
  }

  private revokeCurrentAvatar() {
    if (!this.currentAvatarUrl || !this.currentAvatarUrl.startsWith('blob:')) return;
    URL.revokeObjectURL(this.currentAvatarUrl);
    this.currentAvatarUrl = '';
  }
}
